/* eslint-disable react/jsx-props-no-spreading */
import { FC, ComponentProps } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Colors, Input, Svg, Typography } from '@altermaind/web/ui';

type Props = {
  control: Control<FieldValues>;
  name: string;
  hasError?: boolean;
  errorMessage?: string;
  errorColor?: keyof Colors;
  containerProps?: ComponentProps<typeof Box>;
  additonalProps?: Record<string, unknown>;
  avoidRequiredStarOnLabel?: boolean;
  manipulator?: (value: string) => string;
  labelColor?: keyof Colors;
} & ComponentProps<typeof Input>;

export const WrappedInput: FC<Props> = ({
  control,
  name,
  hasError = false,
  errorMessage,
  errorColor = 'error',
  additonalProps,
  containerProps,
  avoidRequiredStarOnLabel,
  manipulator,
  ...props
}) => {
  const tShared = useTranslations();

  return (
    <Box {...containerProps}>
      <Controller
        control={control}
        name={name}
        rules={{ ...additonalProps }}
        render={({ field }) => (
          <Input
            {...field}
            {...props}
            label={
              !avoidRequiredStarOnLabel && additonalProps?.required
                ? `*${props.label}`
                : props.label
            }
            onChange={event => {
              const manipulatedValue = manipulator
                ? manipulator(event.target.value)
                : event?.target?.value;
              field.onChange({
                ...event,
                target: {
                  ...event.target,
                  value: manipulatedValue,
                },
              });
            }}
            error={hasError ? errorMessage || tShared('inputs.requiredError') : undefined}
          />
        )}
      />
      {hasError && errorMessage && (
        <Box display='flex' flexDirection='row' alignItems='flex-start' mt='xxs'>
          <Svg name='cross' size='xs' fill={errorColor} flex={0} />
          <Typography
            variant='p3/light'
            mt='none'
            ml='xxxs'
            color={errorColor}
            flex={1}
            fontSize='xxs'
            lineHeight='xxxs'
            data-testid={`${props['data-testid']}-error`}
          >
            {errorMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
